import {reportRecaptchaFailure} from './reportRecaptchaFailure'
import {RecaptchaEnterprise} from './types'

export const RECAPTCHA_SCRIPT_ID = 'ventrata-recaptcha-enterprise-script'
export const RECAPTCHA_LOAD_TIMEOUT_MS = 15_000
export const RECAPTCHA_READINESS_POLL_MS = 250

let recaptchaReadyPromise: Promise<void> | undefined

/**
 * `grecaptcha.enterprise` is not guaranteed to exist when the script's `load` event
 * fires - it can be assigned on a later browser task. A one-time readiness check in
 * the `load` handler therefore creates a race that turns otherwise valid customers
 * into repeated null-token attempts.
 */
export const recaptchaEnterpriseIsAvailable = () => {
  return (
    typeof window.grecaptcha?.enterprise?.ready === 'function' &&
    typeof window.grecaptcha?.enterprise?.execute === 'function'
  )
}

const getEnterprise = () => window.grecaptcha?.enterprise as RecaptchaEnterprise

/**
 * Loads the score-based reCAPTCHA Enterprise script once, into the document head.
 *
 * Keeps the `load` and `error` events as fast paths, polls for readiness until the
 * overall timeout, and preserves a slow script after a timeout so that a later
 * attempt can adopt it rather than downloading it again.
 */
export const loadRecaptchaEnterprise = (siteKey: string, nonce?: string): Promise<void> => {
  if (recaptchaEnterpriseIsAvailable()) {
    return new Promise((resolve) => getEnterprise().ready(resolve))
  }

  if (recaptchaReadyPromise) {
    return recaptchaReadyPromise
  }

  recaptchaReadyPromise = new Promise<void>((resolve, reject) => {
    let script = document.getElementById(RECAPTCHA_SCRIPT_ID) as HTMLScriptElement | null
    let settled = false
    let timeout: ReturnType<typeof setTimeout>
    let readinessPoll: ReturnType<typeof setInterval>

    const stopWaiting = () => {
      clearTimeout(timeout)
      clearInterval(readinessPoll)
    }

    const fail = (error: Error, {keepScript = false}: {keepScript?: boolean} = {}) => {
      if (settled) {
        return
      }

      settled = true
      stopWaiting()

      if (!keepScript) {
        script?.remove()
      }

      recaptchaReadyPromise = undefined
      reportRecaptchaFailure('recaptcha.loader_failed', error)
      reject(error)
    }

    const adoptEnterprise = () => {
      if (settled || !recaptchaEnterpriseIsAvailable()) {
        return
      }

      getEnterprise().ready(() => {
        if (settled) {
          return
        }

        settled = true
        stopWaiting()
        recaptchaReadyPromise = undefined
        resolve()
      })
    }

    timeout = setTimeout(
      () =>
        fail(new Error('reCAPTCHA Enterprise loading timed out'), {
          // The browser might still be downloading this script. Leave it in place so
          // a later attempt can observe or adopt it.
          keepScript: true,
        }),
      RECAPTCHA_LOAD_TIMEOUT_MS
    )

    readinessPoll = setInterval(adoptEnterprise, RECAPTCHA_READINESS_POLL_MS)

    if (!script) {
      script = document.createElement('script')
      script.id = RECAPTCHA_SCRIPT_ID
      script.src = `https://www.recaptcha.net/recaptcha/enterprise.js?render=${encodeURIComponent(siteKey)}`
      script.async = true
      script.defer = true

      if (nonce) {
        script.nonce = nonce
      }
    }

    script.addEventListener('load', adoptEnterprise, {once: true})
    script.addEventListener('error', () => fail(new Error('Unable to load reCAPTCHA Enterprise')), {once: true})

    if (!script.isConnected) {
      document.head.appendChild(script)
    }

    // Covers Enterprise loaded by another component before these listeners were attached.
    adoptEnterprise()
  })

  return recaptchaReadyPromise
}

/** Test hook: drops the memoised loader promise. */
export const resetRecaptchaLoader = () => {
  recaptchaReadyPromise = undefined
}
